"use client";

import { QuestInteractionHandler } from "@/app/games/(details)/QuestInteractionHandler";
import { GemsBadge } from "@/app/games/(details)/quest-badges";
import { useGameDetails } from "@/app/games/(details)/useGameDetails";
import type {
  CampaignQuest,
  GameWithQuests,
} from "@/components/QuestsV2/types";
import { useQuestCompletionsV2 } from "@/components/QuestsV2/useQuestCompletions";
import { Action, UserJourney } from "@/context/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { Gamepad2, Heart, Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import useSWR from "swr";
import { Button } from "../../button";
import { useHeroCarousel } from "./useHeroCarousel";

export const CarouselButtons = () => {
  const { currentSlide, currentIndex, track } = useHeroCarousel();
  const { isLoggedIn } = usePassportProvider();
  const router = useRouter();

  const {
    completedQuests: clientCompletedQuests,
    mutate: invalidateQuestCompletions,
    isLoading: isLoadingCompletions,
  } = useQuestCompletionsV2();

  const { data: gamesWithQuests = [] } =
    useSWR<GameWithQuests[]>("games-with-quests");

  const completedQuestsForGame = useMemo(() => {
    if (!clientCompletedQuests || !currentSlide.gameId) return [];

    return Object.values(clientCompletedQuests).filter(
      (completion) => completion.game_id === currentSlide.gameId,
    );
  }, [clientCompletedQuests, currentSlide.gameId]);

  const currentGameQuests = useMemo(() => {
    if (!currentSlide.gameId) return [];
    const game = gamesWithQuests?.find((g) => g.gameId === currentSlide.gameId);
    return game?.quests ?? [];
  }, [currentSlide.gameId, gamesWithQuests]);

  const totalGemsAmount = useMemo(() => {
    if (!currentGameQuests || currentGameQuests.length === 0) return 0;
    return currentGameQuests.reduce((sum, quest) => sum + (quest.gems || 0), 0);
  }, [currentGameQuests]);

  const gameData = useMemo(
    () => ({
      quests: currentGameQuests,
      details: {
        game_id: currentSlide.gameId ?? "",
        name: currentSlide.title ?? "",
        game_slug: currentSlide.gameSlug ?? "",
        slug: currentSlide.gameSlug,
        landing_page_url: currentSlide.route ?? "",
      },
    }),
    [currentSlide, currentGameQuests],
  );

  const {
    onQuestClick,
    isWishlisted,
    incompleteQuests,
    inProgressQuest,
    setInProgressQuest,
    completeQuest,
    addToWishlist,
  } = useGameDetails(
    gameData,
    completedQuestsForGame,
    invalidateQuestCompletions,
  );

  const nextIncompleteQuest =
    incompleteQuests.length > 0 ? incompleteQuests[0] : undefined;

  const handleQuestClick = () => {
    if (!nextIncompleteQuest) {
      if (currentSlide.route) {
        router.push(currentSlide.route);
      }
      return;
    }
    onQuestClick(nextIncompleteQuest);
  };

  const handleCompleteQuest = async (quest: CampaignQuest) => {
    const wasCompleted = await completeQuest(quest);
    if (wasCompleted) {
      await invalidateQuestCompletions();
    }
  };

  // Show loading state while fetching quest completions
  if (isLoadingCompletions) {
    return (
      <div className="flex flex-col md:flex-row items-center gap-3 justify-center md:justify-start">
        <Button
          size="lg"
          className="px-4 py-2 text-sm md:px-10 md:py-3 md:text-base rounded-lg shadow-sm"
          disabled
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="ml-2">Loading...</span>
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row items-center gap-3 justify-center md:justify-start">
        {!isLoggedIn && (
          <Button
            size="lg"
            className="px-4 py-2 text-sm md:px-10 md:py-3 md:text-base rounded-lg shadow-sm"
            onClick={addToWishlist}
          >
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4" />
              <span>Follow Now</span>
            </div>
          </Button>
        )}
        {isLoggedIn && nextIncompleteQuest && (
          <Button
            size="lg"
            className="px-4 py-2 text-sm md:px-10 md:py-3 md:text-base rounded-lg shadow-sm"
            onClick={handleQuestClick}
          >
            <div className="flex items-center gap-2 md:gap-3">
              <Gamepad2 className="w-4 h-4 md:w-5 md:h-5" />
              <span className="font-medium">
                {nextIncompleteQuest.name || "Play Now"}
              </span>
              {nextIncompleteQuest.gems > 0 && (
                <GemsBadge gems={nextIncompleteQuest.gems} />
              )}
            </div>
          </Button>
        )}

        {currentSlide.gameSlug && (
          <Button
            size="lg"
            className="px-4 py-2 text-sm md:px-10 md:py-3 md:text-base rounded-lg shadow-sm"
            onClick={async () => {
              track({
                action: Action.PRESSED,
                extras: {
                  slideId: currentSlide.route,
                  gameId: currentSlide.gameId,
                  gameSlug: currentSlide.gameSlug,
                  route: currentSlide.route,
                  slideIndex: currentIndex,
                  control: "QuestNowButton",
                  controlType: "Button",
                  screen: "HomePageCarousel",
                  userJourney: UserJourney.CAROUSEL,
                },
              });
              router.push(currentSlide.route);
            }}
          >
            <div className="flex items-center gap-2 md:gap-3">
              <Gamepad2 className="w-4 h-4 md:w-5 md:h-5" />
              <span className="font-medium">Quest Now</span>
              {totalGemsAmount > 0 && <GemsBadge gems={totalGemsAmount} />}
            </div>
          </Button>
        )}
      </div>
      <QuestInteractionHandler
        game={gameData}
        completeQuest={handleCompleteQuest}
        inProgressQuest={inProgressQuest}
        setInProgressQuest={setInProgressQuest}
      />
    </>
  );
};
